import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CalendarRange, Ban, ArrowRightLeft, Lock, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fmtTime, money } from "@/lib/format";
import { BookingCard, TransferPanel } from "@/pages/admin/TicketDesk";

const CANCELLABLE = ["confirmed", "reserved"];

function groupByBooking(pax) {
  const m = {};
  for (const p of pax || []) {
    const g = (m[p.code] ||= { code: p.code, names: [], seats: [], cash: p.payment_method === "cash", status: p.status });
    g.names.push(p.name);
    g.seats.push(p.seat);
  }
  return Object.values(m);
}

function PassengerManager({ trip, onChanged }) {
  const [pax, setPax] = useState(null);
  const [transfer, setTransfer] = useState(null);
  const load = useCallback(() => {
    api.get(`/trips/${trip.id}/manifest`).then((r) => setPax(r.data.passengers)).catch((e) => { setPax([]); toast.error(errMsg(e)); });
  }, [trip.id]);
  useEffect(() => { load(); }, [load]);

  const cancelNP = async (code) => {
    if (!window.confirm("Cancel this online ticket under 'Trip not performed'? Full fare + insurance are refunded (no charge).")) return;
    try {
      const { data } = await api.post(`/tickets/${code}/cancel`, { reason: "trip_not_performed", note: "Trip not performed" });
      if (data.pending_cancel || data.approval?.status === "pending") toast.success("Sent for admin approval");
      else toast.success(`Ticket cancelled${data.refund_amount ? ` · refund ${money(data.refund_amount)}` : ""}`);
      load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const openTransfer = async (code) => {
    try { const { data } = await api.get(`/bookings/${code}`); setTransfer(data); } catch (e) { toast.error(errMsg(e)); }
  };

  if (pax === null) return <div className="p-3 text-sm text-slate-400" data-testid={`pax-loading-${trip.id}`}><Loader2 className="inline h-4 w-4 animate-spin" /> Loading passengers…</div>;
  const groups = groupByBooking(pax);
  if (!groups.length) return <div className="p-3 text-sm text-slate-400" data-testid={`pax-empty-${trip.id}`}>No active passengers on this trip.</div>;

  return (
    <div className="space-y-2 bg-slate-50/70 p-3" data-testid={`pax-list-${trip.id}`}>
      {groups.map((g) => (
        <div key={g.code} className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 bg-white p-2.5 text-sm" data-testid={`pax-row-${g.code}`}>
          <span className="font-mono font-bold">{g.code}</span>
          <span className="min-w-0 flex-1">
            <span className="text-slate-700">{g.names.join(", ")}</span>
            <span className="ml-1 text-xs text-slate-400">· seat {g.seats.join(", ")}</span>
          </span>
          <span className={`chip ${g.cash ? "bg-amber-50 text-amber-700" : "bg-sky-50 text-sky-700"}`} data-testid={`pax-method-${g.code}`}>{g.cash ? "Cash" : "Online"}</span>
          {CANCELLABLE.includes(g.status) && (
            g.cash
              ? <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-500" title="A cash ticket can only be cancelled by a teller at the counter" data-testid={`pax-teller-only-${g.code}`}><Lock className="h-3 w-3" />Cancel at teller</span>
              : <Button size="sm" variant="outline" className="h-8 rounded-full border-rose-200 text-rose-600" onClick={() => cancelNP(g.code)} data-testid={`pax-cancel-np-${g.code}`}><Ban className="h-3.5 w-3.5" />Cancel (not performed)</Button>
          )}
          {CANCELLABLE.includes(g.status) && (
            <Button size="sm" variant="outline" className="h-8 rounded-full border-sky-200 text-sky-700" onClick={() => openTransfer(g.code)} data-testid={`pax-transfer-${g.code}`}><ArrowRightLeft className="h-3.5 w-3.5" />Transfer</Button>
          )}
        </div>
      ))}

      <Dialog open={!!transfer} onOpenChange={(o) => { if (!o) setTransfer(null); }}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl" data-testid="pax-transfer-dialog">
          <DialogHeader><DialogTitle className="font-display">Transfer passenger</DialogTitle></DialogHeader>
          {transfer && <BookingCard b={transfer} />}
          {transfer && <TransferPanel b={transfer} onDone={() => { setTransfer(null); load(); onChanged?.(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TripRow({ t, onCancelTrip }) {
  const [open, setOpen] = useState(false);
  const hasPax = t.booked > 0;
  const cancelled = t.status === "cancelled";
  return (
    <div className="rounded-2xl border border-slate-100 bg-white" data-testid={`tt-trip-${t.id}`}>
      <div className="flex flex-wrap items-center gap-3 p-3">
        <div className="font-display text-lg font-extrabold">{fmtTime(t.departure)}</div>
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-semibold text-slate-700">{t.date}</div>
          <div className="text-xs text-slate-500">Bus {t.bus_assigned ? t.bus_number : "not assigned"} · {t.capacity} seats</div>
        </div>
        <span className={`chip capitalize ${cancelled ? "bg-rose-50 text-rose-600" : t.status === "departed" ? "bg-slate-100 text-slate-500" : "bg-emerald-50 text-emerald-700"}`} data-testid={`tt-trip-status-${t.id}`}>{t.status}</span>
        <div className="text-right text-xs" data-testid={`tt-trip-booked-${t.id}`}>
          <div className="font-bold text-slate-800">{t.booked}/{t.capacity} sold</div>
          <div className="text-slate-400">{t.online_booked} online · {t.cash_booked} cash</div>
        </div>
        {hasPax && (
          <Button size="sm" variant="outline" className="rounded-full border-sky-200 text-sky-700" onClick={() => setOpen((o) => !o)} data-testid={`tt-transfer-${t.id}`}>
            <ArrowRightLeft className="h-4 w-4" />{open ? "Hide" : "Transfer / cancel"} ({t.booked})
          </Button>
        )}
        {!cancelled && (
          <Button size="sm" variant="outline" disabled={hasPax} title={hasPax ? "This trip has passengers - transfer or cancel their tickets first" : "Cancel this trip"} className="rounded-full border-rose-200 text-rose-600 disabled:opacity-40" onClick={() => onCancelTrip(t)} data-testid={`tt-cancel-trip-${t.id}`}>
            <Ban className="h-4 w-4" />Cancel trip
          </Button>
        )}
      </div>
      {open && hasPax && <PassengerManager trip={t} onChanged={() => onCancelTrip(null)} />}
    </div>
  );
}

export function TimetableTripsButton({ route }) {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/admin/routes/${route.id}/trips`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, [route.id]);

  const openDlg = () => { setOpen(true); load(); };

  const cancelAll = async () => {
    const n = (d?.trips || []).filter((t) => t.booked === 0 && t.status !== "cancelled").length;
    if (!window.confirm(`Cancel ${n} empty upcoming trip(s) and pause this timetable? Trips that already have passengers are kept, and no new trips will be created until you switch the timetable back on.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/routes/${route.id}/cancel-upcoming`);
      toast.success(`${data.cancelled} trip(s) cancelled${data.kept ? ` · ${data.kept} kept (have passengers)` : ""} · timetable paused`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const cancelTrip = async (t) => {
    if (!t) { load(); return; }
    if (!window.confirm(`Cancel the ${t.date} ${fmtTime(t.departure)} trip?`)) return;
    try { await api.patch(`/admin/trips/${t.id}`, { status: "cancelled", cancel_reason: "Cancelled from timetable" }); toast.success("Trip cancelled"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const trips = d?.trips || [];
  const emptyUpcoming = trips.filter((t) => t.booked === 0 && t.status !== "cancelled").length;

  return (
    <>
      <Button size="icon" variant="ghost" title="View & manage trips" onClick={openDlg} data-testid={`timetable-trips-${route.id}`}>
        <CalendarRange className="h-4 w-4" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto rounded-3xl" data-testid="timetable-trips-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Trips · {d?.route?.name || route.name || "Timetable"}</DialogTitle>
          </DialogHeader>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 p-3">
            <div className="text-sm text-slate-600">
              {d?.route && <span>{d.route.origin_city} → {d.route.destination_city} · </span>}
              <b>{trips.length}</b> trip(s) from today{d && !d.route?.active && <span className="ml-1 chip bg-amber-50 text-amber-700" data-testid="tt-paused-badge">Paused</span>}
            </div>
            <Button size="sm" variant="outline" className="rounded-full border-rose-200 text-rose-600" disabled={busy || emptyUpcoming === 0} onClick={cancelAll} data-testid="tt-cancel-all-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />}Cancel all upcoming trips{emptyUpcoming ? ` (${emptyUpcoming})` : ""}
            </Button>
          </div>

          <div className="flex items-start gap-2 rounded-xl bg-blue-50/60 p-2.5 text-xs text-slate-500" data-testid="tt-rule-hint">
            <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
            Online tickets on a trip that did not run can be cancelled here as "Trip not performed" (full refund). Cash tickets can only be cancelled by a teller at the counter.
          </div>

          {loading && <div className="p-6 text-center text-slate-400" data-testid="tt-loading"><Loader2 className="inline h-5 w-5 animate-spin" /></div>}
          {!loading && trips.length === 0 && <div className="p-6 text-center text-sm text-slate-400" data-testid="tt-empty">No trips scheduled from today for this timetable.</div>}
          <div className="space-y-2" data-testid="tt-trip-list">
            {trips.map((t) => <TripRow key={t.id} t={t} onCancelTrip={cancelTrip} />)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
